import { FC } from 'react';

import { CouponRefundPendingRow } from '@shared/api/couponSettlement/couponSettlement.interface';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { couponCodeLabel } from '../../utils/coupon-settlement.utils';
import { formatDate } from '../../utils/delivery.utils';

export interface CouponRefundPendingTableProps {
  /** 표시 행 (검색·페이지 슬라이스 적용분) */
  rows: CouponRefundPendingRow[];
  /** 빈 상태 문구 — 전체가 없는지/검색 결과만 없는지는 호출부가 판단 */
  emptyMessage: string;
  /** 순번 시작 오프셋 ((page−1)×PAGE_SIZE) */
  startIndex: number;
  /** view 전용이면 체크박스 열 제거 */
  readOnly: boolean;
  /** 제외 선택(쿠폰 id 단위) — 발송 테이블과 단일 Set 공유 (쿠폰 단위 제외 규칙) */
  excl: Set<string>;
  onToggle: (couponId: string) => void;
  /** 헤더 전체 체크 상태/토글 — 검색 결과 기준 (호출부 계산) */
  allChecked: boolean;
  onToggleAll: () => void;
}

/**
 * 미정산 환불 차감 테이블 — 환불 축은 "발송된 건에 대한 환불"만 담는다(스펙 §2).
 * 날짜 셀은 정산완료 상세 모달과 동일한 순서: 구매 → 발송 → 환불.
 */
export const CouponRefundPendingTable: FC<CouponRefundPendingTableProps> = ({
  rows,
  emptyMessage,
  startIndex,
  readOnly,
  excl,
  onToggle,
  allChecked,
  onToggleAll,
}) => {
  // 빈 상태 colSpan — readOnly면 체크박스 열이 없다
  const colSpan = readOnly ? 9 : 10;
  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="w-full table-fixed divide-y divide-gray-100 text-sm">
        <colgroup>
          {!readOnly && <col className="w-[4%]" />}
          <col className="w-[4%]" />
          <col className="w-[9%]" />
          <col className="w-[16%]" />
          <col className="w-[12%]" />
          <col className="w-[11%]" />
          <col className="w-[11%]" />
          <col className="w-[9%]" />
          <col className="w-[13%]" />
          <col className="w-[11%]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-500">
          <tr>
            {!readOnly && (
              <th className="w-10 px-3 py-2 text-center">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={onToggleAll}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </th>
            )}
            <th className="px-3 py-2 text-center">순번</th>
            <th className="px-3 py-2 text-left">쿠폰번호</th>
            <th className="px-3 py-2 text-left">구매상품명</th>
            <th className="px-3 py-2 text-left">
              <p>구매자</p>
              <p className="mt-0.5">연락처</p>
            </th>
            <th className="px-3 py-2 text-left">
              <p>고인명</p>
              <p className="mt-0.5">발인일</p>
            </th>
            <th className="px-3 py-2 text-left">
              <p>구매일/발송일</p>
              <p className="mt-0.5">환불일</p>
            </th>
            <th className="px-3 py-2 text-right">차감액(−)</th>
            <th className="px-3 py-2 text-left">
              <p>소속</p>
              <p className="mt-0.5">지도사명</p>
            </th>
            <th className="px-3 py-2 text-left">지도사연락처</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="px-3 py-6 text-center text-gray-400">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((r, idx) => {
              const included = !excl.has(r.giftCouponId);
              return (
                <tr
                  key={r.giftCouponId}
                  onClick={() => !readOnly && onToggle(r.giftCouponId)}
                  className={
                    readOnly
                      ? 'hover:bg-gray-50'
                      : `cursor-pointer ${
                          included
                            ? 'bg-rose-50/40 hover:bg-rose-50'
                            : 'opacity-50 hover:bg-gray-50'
                        }`
                  }
                >
                  {!readOnly && (
                    <td className="px-3 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={included}
                        readOnly
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </td>
                  )}
                  <td className="px-3 py-3 text-center text-xs tabular-nums text-gray-500">
                    {startIndex + idx + 1}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-700">
                    {couponCodeLabel(r.fullCode)}
                  </td>
                  <td className="px-3 py-3">
                    <p className="break-words text-xs font-medium text-gray-700">
                      {r.purchaseItemName}
                    </p>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <p className="font-bold text-gray-700">{r.buyerName || '-'}</p>
                    {r.buyerPhoneNumber && (
                      <p className="mt-0.5 text-gray-400">
                        {phoneNumberStringFormator(r.buyerPhoneNumber)}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    <p>{r.deceasedName || '-'}</p>
                    <p className="mt-0.5 font-bold text-gray-700">
                      {r.coffinOutDate ? formatDate(r.coffinOutDate) : '-'}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {/* 정산완료 상세 모달과 동일한 순서: 구매 → 발송 → 환불
                        (발송일은 백엔드 deliveredAt 반영 전이면 '-' — §9-4) */}
                    <p>{formatDate(r.purchasedAt)}</p>
                    <p className="mt-0.5 font-bold text-gray-700">
                      {r.deliveredAt ? formatDate(r.deliveredAt) : '-'}
                    </p>
                    <p className="mt-0.5 font-bold text-rose-700">
                      환불 {formatDate(r.refundedAt)}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-rose-700">
                    −{r.amount.toLocaleString('ko-KR')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    <p>{r.teamName || '-'}</p>
                    <p className="mt-0.5 font-bold text-gray-700">
                      {r.workerName || '-'}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {r.workerPhoneNumber
                      ? phoneNumberStringFormator(r.workerPhoneNumber)
                      : '-'}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};
